import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Avatar, Button, Card, Chip, Separator, Spinner } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useState } from "react";

import { authClient } from "@/shared/auth";
import { PageLayout } from "@/shared/components/page-layout.ui.tsx";
import { dt } from "@/shared/helper/datetime";

export const Route = createFileRoute("/dashboard/profile")({
  component: RouteComponent,
});

// Роли better-auth приходят строкой — держим подписи здесь, чтобы страница
// профиля не зависела от локального хелпера из users.tsx.
const roleLabel = (role?: string | null): string => {
  switch (role) {
    case "admin":
      return "Администратор";
    case "courier":
      return "Курьер";
    case "operator":
      return "Оператор";
    case "user":
      return "Пользователь";
    default:
      return "Не определён";
  }
};

// Время профиля показываем в таймзоне Алматы (+5) — как и везде в приложении.
const formatDate = (value?: string | Date | null): string => {
  if (!value) return "—";
  const date = dt(value);

  if (!date.isValid()) return "—";

  return date.format("DD MMMM YYYY, HH:mm");
};

// Строка «поле — значение» в карточке. Значение показываем моноширинным для
// телефона/почты/id, чтобы длинные строки читались ровно.
const Field = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
    <span className="text-sm text-neutral-500">{label}</span>
    <span className="text-sm font-medium">{children}</span>
  </div>
);

function RouteComponent() {
  const navigate = useNavigate();
  const { data, isPending } = authClient.useSession();
  const [isSigningOut, setIsSigningOut] = useState(false);

  const signOut = () => {
    setIsSigningOut(true);
    authClient
      .signOut()
      .then(() => navigate({ to: "/" }))
      .finally(() => setIsSigningOut(false));
  };

  if (isPending) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    );
  }

  const user = data?.user;

  if (!user) {
    return (
      <PageLayout title="Профиль">
        <p className="px-4 text-neutral-500">Не удалось загрузить профиль.</p>
      </PageLayout>
    );
  }

  const name = user.name || "Без имени";
  const phone = ("phoneNumber" in user && (user.phoneNumber as string)) || null;
  const phoneVerified =
    "phoneNumberVerified" in user && Boolean(user.phoneNumberVerified);
  const role = "role" in user ? (user.role as string) : null;

  return (
    <PageLayout
      actions={
        <Button isPending={isSigningOut} variant="outline" onPress={signOut}>
          Выйти
        </Button>
      }
      title="Профиль"
    >
      <div className="px-4">
        <Card className="max-w-2xl">
          <Card.Header className="flex items-center gap-4">
            <Avatar size="lg">
              {user.image ? <Avatar.Image alt={name} src={user.image} /> : null}
              <Avatar.Fallback>{name.charAt(0).toUpperCase()}</Avatar.Fallback>
            </Avatar>

            <div className="flex flex-col gap-1">
              <p className="text-lg font-bold">{name}</p>
              <div>
                <Chip color="accent" size="sm" variant="soft">
                  {roleLabel(role)}
                </Chip>
              </div>
            </div>
          </Card.Header>

          <Card.Content className="flex flex-col gap-4">
            <Separator />

            <Field label="Телефон">
              {phone ? (
                <span className="flex items-center gap-2">
                  <span className="font-mono">{phone}</span>
                  {phoneVerified ? (
                    <Icon
                      className="size-4 text-success-600"
                      icon="solar:verified-check-bold"
                    />
                  ) : (
                    <Chip color="warning" size="sm" variant="soft">
                      Не подтверждён
                    </Chip>
                  )}
                </span>
              ) : (
                "—"
              )}
            </Field>

            <Separator />

            <Field label="Email">
              <span className="font-mono">{user.email || "—"}</span>
            </Field>

            <Separator />

            <Field label="Роль">{roleLabel(role)}</Field>

            <Separator />

            <Field label="ID пользователя">
              <span className="font-mono text-xs">{user.id}</span>
            </Field>

            <Separator />

            <Field label="Дата регистрации">{formatDate(user.createdAt)}</Field>
          </Card.Content>
        </Card>
      </div>
    </PageLayout>
  );
}
